import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RestaurantUnitConversionService from '../../services/restaurant/RestaurantUnitConversionService';

import { AppDispatch, RootState } from '../../store/store';
import {
  closeUnitConversionModal,
  openUnitConversionCreate,
  openUnitConversionEdit,
  setUnitConversionLoading,
  setUnitConversionSaving,
  setUnitConversionPage,
  updateUnitConversionField,
} from '../../store/slices/restaurant/restaurantUnitConversionSlice';

import {
  RestaurantUnitConversionDTO,
  RestaurantUnitConversionFilters,
  RestaurantUnitConversionForm,
} from '../../types/restaurant/RestaurantUnitConversionTypes';
import { RestaurantPageRequest } from '../../types/restaurant/RestaurantRecipeTypes';

import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantUnitConversions = () => {
  const dispatch = useDispatch<AppDispatch>();

  const state = useSelector((root: RootState) => root.restaurantUnitConversion);

  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const requestId = useRef(0);

  const load = async (
    request: RestaurantPageRequest<RestaurantUnitConversionFilters>
  ) => {
    const currentRequest = ++requestId.current;
    dispatch(setUnitConversionLoading(true));

    try {
      const page = await RestaurantUnitConversionService.listFiltered(request);
      if (currentRequest !== requestId.current) return;
      dispatch(setUnitConversionPage(page));
      return page;
    } catch (error) {
      if (currentRequest === requestId.current) handle(error);
    } finally {
      if (currentRequest === requestId.current) {
        dispatch(setUnitConversionLoading(false));
      }
    }
  };

  const edit = async (item: RestaurantUnitConversionDTO) => {
    try {
      const response = await RestaurantUnitConversionService.get(item.id);
      dispatch(openUnitConversionEdit(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const save = async (
    request: RestaurantPageRequest<RestaurantUnitConversionFilters>,
    onEmptyPage: () => void
  ) => {
    if (state.saving) return;

    dispatch(setUnitConversionSaving(true));

    try {
      const response = state.form.id
        ? await RestaurantUnitConversionService.update(
            state.form.id,
            state.form
          )
        : await RestaurantUnitConversionService.create(state.form);

      show(
        response.type || 'success',
        response.title || 'Conversión guardada',
        response.message || 'La conversión de unidad se guardó correctamente.'
      );

      dispatch(closeUnitConversionModal());
      const page = await load(request);
      if (page?.content.length === 0 && request.page > 0) onEmptyPage();
    } catch (error: any) {
      const data = error?.response?.data;

      show(
        data?.type || 'error',
        data?.title || 'Error',
        data?.message || 'No fue posible guardar la conversión.'
      );

      handle(error);
    } finally {
      dispatch(setUnitConversionSaving(false));
    }
  };

  const deactivate = (
    item: RestaurantUnitConversionDTO,
    request: RestaurantPageRequest<RestaurantUnitConversionFilters>,
    onEmptyPage: () => void
  ) =>
    confirm({
      title: 'Desactivar conversión',
      message: `¿Desea desactivar la conversión de ${item.fromUnitName} a ${item.toUnitName}?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          const response = await RestaurantUnitConversionService.updateActive(
            item.id,
            false
          );

          show(
            response.type || 'success',
            response.title || 'Conversión desactivada',
            response.message || 'La conversión fue desactivada correctamente.'
          );

          const page = await load(request);
          if (page?.content.length === 0 && request.page > 0) onEmptyPage();
        } catch (error: any) {
          const data = error?.response?.data;

          show(
            data?.type || 'error',
            data?.title || 'Error',
            data?.message || 'No fue posible actualizar la conversión.'
          );

          handle(error);
        }
      },
    });

  const updateField = <K extends keyof RestaurantUnitConversionForm>(
    field: K,
    value: RestaurantUnitConversionForm[K]
  ) => dispatch(updateUnitConversionField({ field, value }));

  return {
    ...state,
    load,
    edit,
    save,
    deactivate,
    updateField,
    openCreate: () => dispatch(openUnitConversionCreate()),
    closeModal: () => dispatch(closeUnitConversionModal()),
  };
};

export default useRestaurantUnitConversions;
